import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Activity, AlertTriangle, ArrowDown, ArrowUp, Cable, CheckCircle2, ChevronDown, ChevronRight,
  Edit, Loader2, Network, Plus, RotateCcw, Search, Thermometer, Trash2, XCircle, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../utils/admin-api';
import { sessionStorage } from '../../utils/auth-api';
import { BridgePicker, bridgeName, useClientBridges } from './BridgePicker';
import { BulkBridgeAssign } from './BulkBridgeAssign';
import { hexRegister, parseRegisterInput, type VfdItem, type VfdItemKind } from '../../utils/vfd';
import {
  CONTROLLER_ROLE_OPTIONS,
  controllerApi,
  type ControllerPresetBrand,
  type ControllerPresetModel,
  type ControllerTestResult,
  type ProcessController,
} from '../../utils/controllers';

const selectCls = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50';
const cellInputCls = 'h-8 text-sm';
const BAUD_RATES = [1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200];
const COM_PORTS = Array.from({ length: 20 }, (_, i) => `COM${i + 1}`);

const STATUS_BADGE: Record<string, { label: string; cls: string; icon: any }> = {
  verified: { label: 'Verified from manual', cls: 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10', icon: CheckCircle2 },
  partial: { label: 'Partly verified — confirm with Test connection', cls: 'border-amber-500/40 text-amber-600 dark:text-amber-400 bg-amber-500/10', icon: AlertTriangle },
  custom: { label: 'Custom — enter your registers', cls: 'border-slate-500/40 text-muted-foreground bg-muted', icon: Edit },
};

/**
 * Accepts wire addresses (0-based) and manual-style references:
 *   30007 -> FC04 input register 6,   40001 -> FC03 holding register 0,
 *   0x2103 / 2103H -> hex. Returns func only when the text implies it.
 */
const parseControllerAddress = (text: string): { addr: number | null; func?: 3 | 4; invalid?: boolean } => {
  const s = String(text ?? '').trim();
  if (!s) return { addr: null };
  if (/^[34]\d{4}$/.test(s)) {
    const n = Number(s);
    if (n >= 30001 && n <= 39999) return { addr: n - 30001, func: 4 };
    if (n >= 40001 && n <= 49999) return { addr: n - 40001, func: 3 };
  }
  const v = parseRegisterInput(s);
  if (v === null) return { addr: null };
  if (Number.isNaN(v) || v < 0 || v > 65535) return { addr: null, invalid: true };
  return { addr: v };
};

const manualRef = (func: number | undefined, addr: number | null | undefined) =>
  addr === null || addr === undefined ? '' : `${func === 4 ? 30001 + addr : 40001 + addr}`;

type ItemDraft = VfdItem & { _addrText: string; _maskText: string; _open?: boolean };

type Form = {
  bridgeId: string;
  name: string; location: string; description: string;
  brand: string; modelId: string; modelName: string;
  connectionType: 'rtu' | 'tcp'; serialPort: string; baudRate: string; parity: 'none' | 'even' | 'odd';
  dataBits: string; stopBits: string; slaveId: string; ipAddress: string; port: string;
  pollIntervalSec: string; timeoutMs: string; isActive: boolean;
  pvMin: string; pvMax: string; bandAbove: string; bandBelow: string;
};

const emptyForm: Form = {
  bridgeId: '',
  name: '', location: '', description: '', brand: '', modelId: '', modelName: '',
  connectionType: 'rtu', serialPort: 'COM1', baudRate: '9600', parity: 'none', dataBits: '8', stopBits: '1', slaveId: '1',
  ipAddress: '', port: '502', pollIntervalSec: '1', timeoutMs: '1000', isActive: true,
  pvMin: '0', pvMax: '200', bandAbove: '5', bandBelow: '5',
};

let seq = 0;
const newId = () => `ci_${Date.now().toString(36)}_${(seq++).toString(36)}`;
const toDraft = (it: VfdItem): ItemDraft => ({
  ...it,
  id: it.id || newId(),
  _addrText: it.addr === null || it.addr === undefined ? '' : String(it.addr),
  _maskText: it.mask === undefined ? '' : `0x${it.mask.toString(16).toUpperCase()}`,
});
const fromDraft = ({ _addrText, _maskText, _open, ...it }: ItemDraft): VfdItem => {
  const out: VfdItem = { ...it };
  const parsed = parseControllerAddress(_addrText);
  out.addr = parsed.invalid ? null : parsed.addr;
  if (parsed.func) out.func = parsed.func;
  if (it.kind === 'nonzero') {
    const m = parseRegisterInput(_maskText);
    out.mask = m && !Number.isNaN(m) ? m : 0xffff;
  }
  return out;
};
/** High word + low word → IEEE-754 float. */
const wordsToFloat = (hi: number, lo: number) => {
  const view = new DataView(new ArrayBuffer(4));
  view.setUint16(0, hi & 0xffff);
  view.setUint16(2, lo & 0xffff);
  return view.getFloat32(0);
};
const blankItem = (): ItemDraft => toDraft({ id: newId(), name: '', role: '', kind: 'number', addr: null, func: 4, words: 1, signed: true, scale: 1, unit: '', decimals: 1 });

const connectionSummary = (c: ProcessController) => (c.connectionType === 'tcp'
  ? `Modbus TCP · ${c.ipAddress}:${c.port} · unit ${c.slaveId}`
  : `Modbus RTU · ${c.serialPort} · ${c.baudRate} ${c.dataBits}${(c.parity || 'none')[0].toUpperCase()}${c.stopBits} · slave ${c.slaveId}`);

export function ControllersTab() {
  const token = sessionStorage.get()?.token || '';
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState('');
  const bridges = useClientBridges(token, selectedClient);
  const [list, setList] = useState<ProcessController[]>([]);
  const [presets, setPresets] = useState<ControllerPresetBrand[]>([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ProcessController | null>(null);
  const [form, setForm] = useState<Form>(emptyForm);
  const [items, setItems] = useState<ItemDraft[]>([]);
  const [step, setStep] = useState('controller');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<ProcessController | null>(null);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<ControllerTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [exploreStart, setExploreStart] = useState('30001');

  useEffect(() => {
    (async () => {
      try {
        const [cl, pr] = await Promise.all([adminApi.getClients(token), controllerApi.presets(token).catch(() => [] as ControllerPresetBrand[])]);
        setClients(cl);
        setPresets(pr);
        if (cl.length > 0) setSelectedClient(cl[0].id);
      } catch (e: any) {
        toast.error(e.message || 'Failed to load clients');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const load = async (clientId = selectedClient) => {
    if (!clientId) return;
    setLoading(true);
    try {
      setList(await controllerApi.adminList(token, clientId));
    } catch (e: any) {
      toast.error(e.message || 'Failed to load controllers');
      setList([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (selectedClient) load(selectedClient); }, [selectedClient]);

  const brandModels = useMemo(() => presets.find((b) => b.brand === form.brand)?.models || [], [presets, form.brand]);
  const selectedModel: ControllerPresetModel | undefined = brandModels.find((m) => m.id === form.modelId);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const resetTest = () => { setTestResult(null); setTestError(null); };

  const openCreate = () => {
    setEditing(null);
    const brand = presets[0];
    const model = brand?.models[0];
    const d = model?.defaults || {};
    setForm({
      ...emptyForm,
      brand: brand?.brand || '', modelId: model?.id || '', modelName: model?.name || '',
      baudRate: String(d.baudRate ?? 9600), parity: (d.parity as Form['parity']) || 'none',
      dataBits: String(d.dataBits ?? 8), stopBits: String(d.stopBits ?? 1), slaveId: String(d.slaveId ?? 1),
    });
    setItems((model?.items || []).map((it) => toDraft({ ...it, id: newId() })));
    setStep('controller');
    resetTest();
    setOpen(true);
  };

  const openEdit = (c: ProcessController) => {
    setEditing(c);
    const s = (v: any, fb = '') => (v === null || v === undefined ? fb : String(v));
    setForm({
      bridgeId: c.bridgeId || '',
      name: c.name, location: c.location || '', description: c.description || '',
      brand: c.brand || '', modelId: c.modelId || '', modelName: c.modelName || '',
      connectionType: c.connectionType, serialPort: c.serialPort || 'COM1', baudRate: s(c.baudRate, '9600'),
      parity: (c.parity || 'none') as Form['parity'], dataBits: s(c.dataBits, '8'), stopBits: s(c.stopBits, '1'),
      slaveId: s(c.slaveId, '1'), ipAddress: c.ipAddress || '', port: s(c.port, '502'),
      pollIntervalSec: s((c.pollIntervalMs || 1000) / 1000), timeoutMs: s(c.timeoutMs, '1000'), isActive: c.isActive !== false,
      pvMin: s(c.display?.pvMin, '0'), pvMax: s(c.display?.pvMax, '200'), bandAbove: s(c.display?.bandAbove, '5'), bandBelow: s(c.display?.bandBelow, '5'),
    });
    setItems((c.items || []).map(toDraft));
    setStep('controller');
    resetTest();
    setOpen(true);
  };

  const applyPreset = (brand: string, modelId: string) => {
    const model = presets.find((b) => b.brand === brand)?.models.find((m) => m.id === modelId);
    if (model && items.length > 0 && !window.confirm(`Load the ${brand} ${model.name} preset? This replaces the current register map.`)) return;
    setForm((f) => ({ ...f, brand, modelId, modelName: model?.name || '' }));
    if (model) setItems(model.items.map((it) => toDraft({ ...it, id: newId() })));
  };

  const updateItem = (id: string, patch: Partial<ItemDraft>) => setItems((l) => l.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const moveItem = (i: number, d: number) => setItems((l) => {
    const n = [...l]; const j = i + d;
    if (j < 0 || j >= n.length) return l;
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });

  const errors = useMemo(() => {
    const out: string[] = [];
    const names = new Set<string>();
    items.forEach((it, i) => {
      const label = it.name.trim() || `Row ${i + 1}`;
      if (!it.name.trim()) out.push(`Row ${i + 1}: name is required`);
      const k = it.name.trim().toLowerCase();
      if (k && names.has(k)) out.push(`"${label}": duplicate name`);
      names.add(k);
      if (parseControllerAddress(it._addrText).invalid) out.push(`"${label}": invalid address`);
    });
    return out;
  }, [items]);

  const connectionPayload = () => ({
    bridgeId: form.bridgeId || null,
    connectionType: form.connectionType,
    serialPort: form.serialPort.trim().toUpperCase(),
    baudRate: Number(form.baudRate),
    parity: form.parity,
    dataBits: Number(form.dataBits),
    stopBits: Number(form.stopBits),
    slaveId: Number(form.slaveId),
    ipAddress: form.ipAddress.trim(),
    port: Number(form.port),
    timeoutMs: Number(form.timeoutMs || 1000),
  });

  const runTest = async (withExplore: boolean) => {
    if (!selectedClient) return;
    if (form.connectionType === 'rtu' && !form.serialPort.trim()) { toast.error('Enter the COM port first'); return; }
    if (form.connectionType === 'tcp' && !form.ipAddress.trim()) { toast.error('Enter the IP address first'); return; }
    const start = parseControllerAddress(exploreStart);
    setTesting(true);
    setTestError(null);
    if (!withExplore) setTestResult(null);
    try {
      const r = await controllerApi.test(token, selectedClient, {
        ...connectionPayload(),
        id: editing?.id,
        name: form.name || 'Controller',
        items: items.map(fromDraft),
        explore: withExplore ? { start: start.addr ?? 0, count: 30 } : null,
      });
      setTestResult(r);
      if (r.ok) toast.success('Controller answered', { description: r.link });
      else toast.error('Test failed', { description: r.error || 'No reply' });
    } catch (e: any) {
      setTestError(e.message || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  // Register explorer → assign an address to the row with this role (create it if missing).
  // asFloat: this register and the next hold a 32-bit float, low word first.
  const useAs = (role: 'pv' | 'sv' | 'output', func: 3 | 4, addr: number, asFloat = false) => {
    const format: Partial<ItemDraft> = asFloat
      ? { words: 2, float: true, signed: false, wordOrder: 'little', scale: 1 }
      : { words: 1, float: false, ...(role === 'output' ? {} : { signed: true, scale: 0.1 }) };
    setItems((l) => {
      const idx = l.findIndex((it) => it.role === role);
      const ref = String(addr);
      if (idx >= 0) {
        const n = [...l];
        n[idx] = { ...n[idx], func, _addrText: ref, ...format };
        return n;
      }
      const label = role === 'pv' ? 'Process Value' : role === 'sv' ? 'Set Point' : 'Output';
      return [...l, { ...blankItem(), name: label, role, func, _addrText: ref, scale: role === 'output' ? 1 : 0.1, unit: role === 'output' ? '%' : '°C', decimals: role === 'output' ? 0 : 1, ...format }];
    });
    toast.success(`${role.toUpperCase()} set to ${func === 4 ? 30001 + addr : 40001 + addr} (FC0${func} #${addr}${asFloat ? ', float' : ''})`);
  };

  const save = async () => {
    if (!form.name.trim()) { setStep('controller'); toast.error('Controller name is required'); return; }
    if (form.connectionType === 'rtu' && !form.serialPort.trim()) { setStep('connection'); toast.error('COM port is required'); return; }
    if (form.connectionType === 'tcp' && !form.ipAddress.trim()) { setStep('connection'); toast.error('IP address is required'); return; }
    if (errors.length) { setStep('registers'); toast.error(errors[0]); return; }
    const payload: Record<string, any> = {
      name: form.name.trim(), location: form.location.trim(), description: form.description.trim(),
      brand: form.brand, modelId: form.modelId, modelName: form.modelName,
      ...connectionPayload(),
      pollIntervalMs: Math.round(Number(form.pollIntervalSec || 1) * 1000),
      isActive: form.isActive,
      display: { pvMin: Number(form.pvMin), pvMax: Number(form.pvMax), bandAbove: Number(form.bandAbove), bandBelow: Number(form.bandBelow) },
      items: items.map(fromDraft),
    };
    setSaving(true);
    try {
      if (editing) {
        await controllerApi.update(token, selectedClient, editing.id, payload);
        toast.success('Controller updated', { description: 'The bridge picks up changes within about a minute.' });
      } else {
        await controllerApi.create(token, selectedClient, payload);
        toast.success('Controller added', { description: 'The bridge starts polling it within about a minute.' });
      }
      setOpen(false);
      load();
    } catch (e: any) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c: ProcessController) => {
    try { await controllerApi.update(token, selectedClient, c.id, { isActive: !(c.isActive !== false) }); load(); } catch (e: any) { toast.error(e.message); }
  };
  const confirmDelete = async () => {
    if (!deleting) return;
    try { await controllerApi.remove(token, selectedClient, deleting.id); toast.success(`Deleted ${deleting.name}`); setDeleting(null); load(); } catch (e: any) { toast.error(e.message); }
  };

  // ------------------------------------------------------------------ test panel
  const TestPanel = (
    <div className="rounded-lg border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3">
        <div className="flex items-center gap-2 text-sm font-semibold"><Zap className="h-4 w-4 text-amber-500" /> Test connection</div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" size="sm" onClick={() => runTest(false)} disabled={testing}>
            {testing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Activity className="mr-1.5 h-3.5 w-3.5" />} Test now
          </Button>
          <div className="flex items-center gap-1">
            <Input className="h-8 w-[90px] font-mono text-xs" value={exploreStart} onChange={(e) => setExploreStart(e.target.value)} title="First register to scan (e.g. 30001)" />
            <Button type="button" size="sm" variant="outline" onClick={() => runTest(true)} disabled={testing}>
              <Search className="mr-1.5 h-3.5 w-3.5" /> Register explorer
            </Button>
          </div>
        </div>
      </div>
      <div className="p-3 text-sm">
        {!testResult && !testError && !testing && (
          <p className="text-muted-foreground">
            The bridge at the site reads the controller using the settings above — nothing is saved or written. Use <b>Register explorer</b> to scan 30 input (FC04) and holding (FC03) registers and match them against the controller display.
          </p>
        )}
        {testing && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Asking the site bridge to read the controller… (up to 45 s)
          </div>
        )}
        {testError && (
          <div className="flex items-start gap-2 text-red-600 dark:text-red-400"><XCircle className="mt-0.5 h-4 w-4 shrink-0" /> {testError}</div>
        )}
        {testResult && !testing && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
            <div className={`flex items-start gap-2 rounded-md border p-2.5 ${testResult.ok ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-red-500/40 bg-red-500/10'}`}>
              {testResult.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />}
              <div>
                <div className="font-semibold">{testResult.ok ? 'Controller is answering' : 'No valid reply'}</div>
                <div className="text-xs text-muted-foreground">
                  {testResult.link}{testResult.elapsedMs != null ? ` · ${testResult.elapsedMs} ms on the bridge` : ''}{testResult.roundTripMs != null ? ` · ${testResult.roundTripMs} ms round trip` : ''}
                </div>
                {testResult.error && <div className="mt-1 text-xs text-red-600 dark:text-red-400">{testResult.error}</div>}
              </div>
            </div>

            {testResult.items?.length > 0 && (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr className="text-left"><th className="px-2 py-1.5">Register</th><th className="px-2 py-1.5">Address</th><th className="px-2 py-1.5 text-right">Raw</th><th className="px-2 py-1.5 text-right">Value</th><th className="px-2 py-1.5">Status</th></tr>
                  </thead>
                  <tbody>
                    {testResult.items.map((r) => (
                      <tr key={r.name} className="border-t">
                        <td className="px-2 py-1.5 font-medium">{r.name}</td>
                        <td className="px-2 py-1.5 font-mono">{r.addr === null || r.addr === undefined ? '—' : `${manualRef(r.func, r.addr)} (FC0${r.func} #${r.addr})`}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{r.raw ?? '—'}</td>
                        <td className="px-2 py-1.5 text-right font-semibold tabular-nums">{r.value ?? '—'}</td>
                        <td className="px-2 py-1.5">{r.ok ? <span className="text-emerald-600 dark:text-emerald-400">✓ OK</span> : <span className={r.error === 'No address set' ? 'text-muted-foreground' : 'text-red-600 dark:text-red-400'}>{r.error}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {testResult.explore && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Compare with the controller display. Example: display <b>125.4</b> → raw <b>1254</b> (÷10). Selec PID500 sends <b>floats</b> across two registers (low word first) — check the <b>Float</b> column and use the green <b>PV f / SV f</b> buttons. Click a role to put that address into the register map.
                </p>
                <div className="grid gap-3 lg:grid-cols-2">
                  {([['Input registers (FC04)', 4, testResult.explore.input], ['Holding registers (FC03)', 3, testResult.explore.holding]] as const).map(([title, func, rows]) => (
                    <div key={title} className="overflow-hidden rounded-md border">
                      <div className="bg-muted/50 px-2 py-1.5 text-xs font-semibold">{title}</div>
                      <div className="max-h-72 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="sticky top-0 bg-card text-muted-foreground">
                            <tr className="text-left"><th className="px-2 py-1">Addr</th><th className="px-2 py-1 text-right">Raw</th><th className="px-2 py-1 text-right">Signed</th><th className="px-2 py-1 text-right">÷10</th><th className="px-2 py-1 text-right" title="This register (low word) + next register (high word) as a 32-bit float">Float</th><th className="px-2 py-1">Use as</th></tr>
                          </thead>
                          <tbody>
                            {rows.map((e, ri) => {
                              const signed = e.raw !== undefined && e.raw >= 32768 ? e.raw - 65536 : e.raw;
                              const next = rows[ri + 1];
                              const flt = e.raw !== undefined && next?.raw !== undefined && next.addr === e.addr + 1 ? wordsToFloat(next.raw, e.raw) : null;
                              const fltOk = flt !== null && Number.isFinite(flt) && Math.abs(flt) < 1e6 && (flt === 0 || Math.abs(flt) > 1e-4);
                              const nonZero = e.raw !== undefined && e.raw !== 0;
                              return (
                                <tr key={e.addr} className={`border-t ${nonZero ? '' : 'text-muted-foreground'}`}>
                                  <td className="px-2 py-1 font-mono">{func === 4 ? 30001 + e.addr : 40001 + e.addr}</td>
                                  {e.raw === undefined ? (
                                    <td colSpan={4} className="px-2 py-1 text-[11px] italic">{e.error}</td>
                                  ) : (
                                    <>
                                      <td className="px-2 py-1 text-right font-mono">{e.raw}</td>
                                      <td className="px-2 py-1 text-right font-mono">{signed}</td>
                                      <td className="px-2 py-1 text-right font-mono">{((signed ?? 0) / 10).toFixed(1)}</td>
                                      <td className={`px-2 py-1 text-right font-mono ${fltOk && flt !== 0 ? 'font-semibold text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>{fltOk ? Number(flt!.toFixed(3)) : '—'}</td>
                                    </>
                                  )}
                                  <td className="px-2 py-1">
                                    {e.raw !== undefined && (
                                      <div className="flex gap-1">
                                        {(['pv', 'sv', 'output'] as const).map((role) => (
                                          <button key={role} type="button" onClick={() => useAs(role, func, e.addr)} className="rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase hover:bg-primary hover:text-primary-foreground">
                                            {role}
                                          </button>
                                        ))}
                                        {fltOk && flt !== 0 && (['pv', 'sv'] as const).map((role) => (
                                          <button key={`f-${role}`} type="button" title={`Use as ${role.toUpperCase()} (32-bit float)`} onClick={() => useAs(role, func, e.addr, true)} className="rounded border border-emerald-500/50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-600 hover:bg-emerald-600 hover:text-white dark:text-emerald-400">
                                            {role} f
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );

  // ------------------------------------------------------------------ render
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold"><Thermometer className="h-6 w-6" /> Controller Connection</h2>
          <p className="text-sm text-muted-foreground">
            Process / PID controllers (e.g. Selec PID500-U) over Modbus RTU (RS-485). Clients see them under Live PLC Data → Controllers.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select className={`${selectCls} sm:w-[220px]`} value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)} aria-label="Client">
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <BulkBridgeAssign token={token} clientId={selectedClient} onDone={() => load()} />
          <Button onClick={openCreate} disabled={!selectedClient}><Plus className="mr-2 h-4 w-4" /> Add controller</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…</div>
      ) : list.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <Thermometer className="h-12 w-12 text-muted-foreground opacity-50" />
            <div className="text-lg font-semibold">No controllers for this client</div>
            <p className="max-w-md text-sm text-muted-foreground">
              Add a controller, pick the Selec PID500-U preset, set the COM port, baud rate and slave ID to match the controller's communication level, then use Test connection.
            </p>
            <Button onClick={openCreate} disabled={!selectedClient}><Plus className="mr-2 h-4 w-4" /> Add the first controller</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {list.map((c) => {
            const regs = (c.items || []).filter((it) => it.kind !== 'derived');
            const unmapped = regs.filter((it) => it.addr === null || it.addr === undefined).length;
            return (
              <Card key={c.id} className={c.isActive === false ? 'opacity-60' : ''}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="truncate text-lg">{c.name}</CardTitle>
                      <CardDescription className="truncate">{[c.brand, c.modelName].filter(Boolean).join(' · ') || 'Custom controller'}{c.location ? ` — ${c.location}` : ''}</CardDescription>
                    </div>
                    <Switch checked={c.isActive !== false} onCheckedChange={() => toggleActive(c)} aria-label="Active" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {c.connectionType === 'tcp' ? <Network className="h-4 w-4" /> : <Cable className="h-4 w-4" />}
                    <span className="truncate">{connectionSummary(c)} · {bridgeName(bridges, c.bridgeId) || 'any bridge PC'}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="rounded-md bg-muted px-2 py-1">{regs.length} registers</span>
                    <span className="rounded-md bg-muted px-2 py-1">every {((c.pollIntervalMs || 1000) / 1000).toFixed(1)} s</span>
                    {unmapped > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-amber-600 dark:text-amber-400">
                        <AlertTriangle className="h-3 w-3" /> {unmapped} without address
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(c)}><Edit className="mr-1.5 h-4 w-4" /> Edit / test</Button>
                    <Button variant="outline" size="sm" onClick={() => setDeleting(c)} aria-label={`Delete ${c.name}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ---------------- add / edit ---------------- */}
      <Dialog open={open} onOpenChange={(o) => !saving && !testing && setOpen(o)}>
        <DialogContent className="max-h-[92vh] w-[96vw] overflow-y-auto sm:max-w-[1200px] [&>*]:min-w-0">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add controller'}</DialogTitle>
            <DialogDescription>Only reads are performed — the dashboard never writes to the controller.</DialogDescription>
          </DialogHeader>

          <Tabs value={step} onValueChange={setStep}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="controller">1 · Controller</TabsTrigger>
              <TabsTrigger value="connection">2 · Connection & test</TabsTrigger>
              <TabsTrigger value="registers">3 · Registers ({items.length}){errors.length > 0 && <AlertTriangle className="ml-1.5 h-3.5 w-3.5 text-amber-500" />}</TabsTrigger>
            </TabsList>

            {/* Step 1 */}
            <TabsContent value="controller" className="space-y-5 pt-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="ctl-name">Controller name *</Label>
                  <Input id="ctl-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Oven Zone 1 PID" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ctl-loc">Location / equipment</Label>
                  <Input id="ctl-loc" value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="e.g. Curing oven, Line 1" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ctl-desc">Description</Label>
                <Textarea id="ctl-desc" rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Optional notes shown on the client screen" />
              </div>
              <div className="rounded-lg border p-4">
                <div className="mb-3 text-sm font-semibold">Brand & model</div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Brand</Label>
                    <select className={selectCls} value={form.brand} onChange={(e) => { const b = e.target.value; applyPreset(b, presets.find((x) => x.brand === b)?.models[0]?.id || ''); }}>
                      {presets.map((b) => <option key={b.brand} value={b.brand}>{b.brand}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Model</Label>
                    <select className={selectCls} value={form.modelId} onChange={(e) => applyPreset(form.brand, e.target.value)}>
                      {brandModels.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                </div>
                {selectedModel && (() => {
                  const b = STATUS_BADGE[selectedModel.status] || STATUS_BADGE.custom;
                  const Icon = b.icon;
                  return (
                    <div className="mt-3 space-y-2">
                      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${b.cls}`}><Icon className="h-3.5 w-3.5" /> {b.label}</span>
                      {selectedModel.note && <p className="text-sm text-muted-foreground">{selectedModel.note}</p>}
                      <Button type="button" variant="outline" size="sm" onClick={() => applyPreset(form.brand, form.modelId)}><RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reload preset register map</Button>
                    </div>
                  );
                })()}
              </div>
              <div className="rounded-lg border p-4">
                <div className="mb-1 text-sm font-semibold">Display range</div>
                <p className="mb-3 text-xs text-muted-foreground">Sets the client gauge scale and the "on target" band around the set point.</p>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  {([['pvMin', 'PV scale min'], ['pvMax', 'PV scale max'], ['bandBelow', 'On-target band below SV'], ['bandAbove', 'On-target band above SV']] as const).map(([k, l]) => (
                    <div key={k} className="space-y-1.5">
                      <Label className="text-xs">{l}</Label>
                      <Input type="number" step="any" value={form[k]} onChange={(e) => set(k, e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Step 2 */}
            <TabsContent value="connection" className="space-y-5 pt-4">
              <div className="grid grid-cols-2 gap-3">
                {([
                  { id: 'rtu', title: 'Modbus RTU', sub: 'RS-485 via USB/serial adapter on the bridge PC', icon: Cable },
                ] as const).map((o) => (
                  <button key={o.id} type="button" onClick={() => set('connectionType', o.id)} aria-pressed={form.connectionType === o.id}
                    className={`flex items-start gap-3 rounded-lg border p-4 text-left transition-colors ${form.connectionType === o.id ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:border-primary/50'}`}>
                    <o.icon className="mt-0.5 h-5 w-5" />
                    <div><div className="font-semibold">{o.title}</div><div className="text-xs text-muted-foreground">{o.sub}</div></div>
                  </button>
                ))}
              </div>

              <BridgePicker bridges={bridges} value={form.bridgeId} onChange={(v) => set('bridgeId', v)} id="ctl-bridge-pc" />

              {form.connectionType === 'rtu' ? (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label>COM port *</Label>
                    <Input list="ctl-com-ports" value={form.serialPort} onChange={(e) => set('serialPort', e.target.value)} placeholder="COM3" />
                    <datalist id="ctl-com-ports">{COM_PORTS.map((p) => <option key={p} value={p} />)}</datalist>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Baud rate</Label>
                    <select className={selectCls} value={form.baudRate} onChange={(e) => set('baudRate', e.target.value)}>{BAUD_RATES.map((b) => <option key={b} value={b}>{b}</option>)}</select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Slave ID (station no.)</Label>
                    <Input type="number" min={1} max={247} value={form.slaveId} onChange={(e) => set('slaveId', e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Parity</Label>
                    <select className={selectCls} value={form.parity} onChange={(e) => set('parity', e.target.value as Form['parity'])}>
                      <option value="none">None</option><option value="even">Even</option><option value="odd">Odd</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Data bits</Label>
                    <select className={selectCls} value={form.dataBits} onChange={(e) => set('dataBits', e.target.value)}><option value="8">8</option><option value="7">7</option></select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Stop bits</Label>
                    <select className={selectCls} value={form.stopBits} onChange={(e) => set('stopBits', e.target.value)}><option value="1">1</option><option value="2">2</option></select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                  <div className="space-y-1.5"><Label>IP address *</Label><Input value={form.ipAddress} onChange={(e) => set('ipAddress', e.target.value)} placeholder="192.168.1.60" /></div>
                  <div className="space-y-1.5"><Label>Port</Label><Input type="number" value={form.port} onChange={(e) => set('port', e.target.value)} /></div>
                  <div className="space-y-1.5"><Label>Unit ID (station no.)</Label><Input type="number" min={1} max={247} value={form.slaveId} onChange={(e) => set('slaveId', e.target.value)} /></div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                <div className="space-y-1.5"><Label>Update every (seconds)</Label><Input type="number" min={0.5} max={60} step={0.5} value={form.pollIntervalSec} onChange={(e) => set('pollIntervalSec', e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Reply timeout (ms)</Label><Input type="number" min={200} max={10000} step={100} value={form.timeoutMs} onChange={(e) => set('timeoutMs', e.target.value)} /></div>
                <div className="flex items-end gap-3 pb-1">
                  <Switch id="ctl-active" checked={form.isActive} onCheckedChange={(v) => set('isActive', v)} />
                  <Label htmlFor="ctl-active">Active (polled by bridge)</Label>
                </div>
              </div>

              {TestPanel}

              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                <div className="mb-1 font-medium text-foreground">Selec PID500-U tips</div>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Baud rate, parity, stop bit and station number are in the controller's Level 5 (communication) menu — match them here.</li>
                  <li>Up to 32 controllers can share one RS-485 line; give each a different station number. Terminate the far end (about 100 Ω).</li>
                  <li>If the test says "did not answer", swap the A/B (+/−) wires first, then re-check the station number and baud rate.</li>
                </ul>
              </div>
            </TabsContent>

            {/* Step 3 */}
            <TabsContent value="registers" className="space-y-3 pt-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">
                  Type addresses as in the manual (<code>30007</code> = input register, <code>40001</code> = holding register) or as 0-based wire addresses with the function chosen.
                </p>
                <Button type="button" variant="outline" size="sm" onClick={() => setItems((l) => [...l, { ...blankItem(), _open: true }])}><Plus className="mr-1.5 h-3.5 w-3.5" /> Register</Button>
              </div>
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full min-w-[960px] text-sm">
                  <thead className="bg-muted/50 text-xs text-muted-foreground">
                    <tr className="text-left">
                      <th className="w-8 px-2 py-2" />
                      <th className="px-2 py-2 font-medium">Name</th>
                      <th className="px-2 py-2 font-medium">Shows as</th>
                      <th className="px-2 py-2 font-medium">Type</th>
                      <th className="px-2 py-2 font-medium">Function</th>
                      <th className="px-2 py-2 font-medium">Address</th>
                      <th className="px-2 py-2 font-medium">Scale</th>
                      <th className="px-2 py-2 font-medium">Unit</th>
                      <th className="px-2 py-2 font-medium">Dec.</th>
                      <th className="w-24 px-2 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">No registers. Add one, pick a preset, or use Register explorer.</td></tr>}
                    {items.map((it, i) => {
                      const parsed = parseControllerAddress(it._addrText);
                      const func = parsed.func ?? (it.func === 4 ? 4 : 3);
                      const flag = it.kind === 'bit' || it.kind === 'nonzero';
                      return [
                        <tr key={it.id} className={`border-t ${it.hidden ? 'opacity-60' : ''}`}>
                          <td className="px-2 py-1.5">
                            <button type="button" className="rounded p-1 hover:bg-muted" onClick={() => updateItem(it.id, { _open: !it._open })} aria-label="More options" aria-expanded={!!it._open}>
                              {it._open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          </td>
                          <td className="px-2 py-1.5"><Input className={`${cellInputCls} min-w-[150px]`} value={it.name} onChange={(e) => updateItem(it.id, { name: e.target.value })} placeholder="e.g. Process Value" /></td>
                          <td className="px-2 py-1.5">
                            <select className={`${selectCls} h-8 min-w-[150px]`} value={it.role} onChange={(e) => updateItem(it.id, { role: e.target.value })}>
                              {CONTROLLER_ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-1.5">
                            <select className={`${selectCls} h-8 min-w-[120px]`} value={it.kind} onChange={(e) => {
                              const kind = e.target.value as VfdItemKind;
                              updateItem(it.id, kind === 'bit' || kind === 'nonzero' ? { kind, trueText: it.trueText || 'ALARM', falseText: it.falseText || 'NORMAL', badWhenTrue: true } : { kind });
                            }}>
                              <option value="number">Number</option>
                              <option value="bit">Status bit</option>
                              <option value="nonzero">Flag (≠0 = on)</option>
                            </select>
                          </td>
                          <td className="px-2 py-1.5">
                            <select className={`${selectCls} h-8 min-w-[112px]`} value={func} disabled={!!parsed.func} onChange={(e) => updateItem(it.id, { func: Number(e.target.value) as 3 | 4 })}>
                              <option value={4}>04 Input</option>
                              <option value={3}>03 Holding</option>
                            </select>
                          </td>
                          <td className="px-2 py-1.5">
                            <Input className={`${cellInputCls} w-[110px] font-mono ${parsed.invalid ? 'border-red-500' : ''}`} value={it._addrText} onChange={(e) => updateItem(it.id, { _addrText: e.target.value })} placeholder="—" aria-invalid={parsed.invalid} />
                            {parsed.addr !== null && !parsed.invalid && (
                              <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">{manualRef(func, parsed.addr)} · #{parsed.addr} · {hexRegister(parsed.addr)}</div>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            <select className={`${selectCls} h-8 min-w-[88px]`} value={String(it.scale ?? 1)} disabled={flag} onChange={(e) => updateItem(it.id, { scale: Number(e.target.value) })}>
                              {['1', '0.1', '0.01', '0.001', '10'].map((s) => <option key={s} value={s}>× {s}</option>)}
                              {!['1', '0.1', '0.01', '0.001', '10'].includes(String(it.scale ?? 1)) && <option value={String(it.scale)}>× {it.scale}</option>}
                            </select>
                          </td>
                          <td className="px-2 py-1.5"><Input className={`${cellInputCls} w-[70px]`} value={it.unit || ''} disabled={flag} onChange={(e) => updateItem(it.id, { unit: e.target.value })} placeholder="unit" /></td>
                          <td className="px-2 py-1.5"><Input className={`${cellInputCls} w-[56px]`} type="number" min={0} max={6} value={it.decimals ?? 1} disabled={flag} onChange={(e) => updateItem(it.id, { decimals: Number(e.target.value) })} /></td>
                          <td className="px-2 py-1.5">
                            <div className="flex justify-end gap-0.5">
                              <button type="button" className="rounded p-1 hover:bg-muted disabled:opacity-30" disabled={i === 0} onClick={() => moveItem(i, -1)} aria-label="Move up"><ArrowUp className="h-3.5 w-3.5" /></button>
                              <button type="button" className="rounded p-1 hover:bg-muted disabled:opacity-30" disabled={i === items.length - 1} onClick={() => moveItem(i, 1)} aria-label="Move down"><ArrowDown className="h-3.5 w-3.5" /></button>
                              <button type="button" className="rounded p-1 hover:bg-muted" onClick={() => setItems((l) => l.filter((x) => x.id !== it.id))} aria-label="Delete row"><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
                            </div>
                          </td>
                        </tr>,
                        it._open && (
                          <tr key={`${it.id}-more`} className="bg-muted/30">
                            <td />
                            <td colSpan={9} className="px-2 py-3">
                              <div className="grid gap-3 md:grid-cols-4">
                                <label className="flex items-center gap-2 text-sm"><Switch checked={!!it.signed} onCheckedChange={(v) => updateItem(it.id, { signed: v })} /> Signed (negative values)</label>
                                <div className="space-y-1">
                                  <Label className="text-xs">Size</Label>
                                  <select className={`${selectCls} h-8`} value={it.float ? 'float' : String(it.words || 1)} onChange={(e) => {
                                    const v = e.target.value;
                                    updateItem(it.id, v === 'float' ? { words: 2, float: true, signed: false } : { words: Number(v) as 1 | 2, float: false });
                                  }}>
                                    <option value="1">16-bit integer</option><option value="2">32-bit integer</option><option value="float">32-bit float</option>
                                  </select>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Word order (32-bit)</Label>
                                  <select className={`${selectCls} h-8`} disabled={(it.words || 1) !== 2} value={it.wordOrder || 'big'} onChange={(e) => updateItem(it.id, { wordOrder: e.target.value as 'big' | 'little' })}>
                                    <option value="big">High word first</option><option value="little">Low word first (LSB, MSB)</option>
                                  </select>
                                </div>
                                {it.kind === 'bit' && (
                                  <div className="space-y-1"><Label className="text-xs">Bit number (0–15)</Label><Input className={cellInputCls} type="number" min={0} max={31} value={it.bit ?? 0} onChange={(e) => updateItem(it.id, { bit: Number(e.target.value) })} /></div>
                                )}
                                {flag && (
                                  <>
                                    <div className="space-y-1"><Label className="text-xs">Text when ON</Label><Input className={cellInputCls} value={it.trueText || ''} onChange={(e) => updateItem(it.id, { trueText: e.target.value })} /></div>
                                    <div className="space-y-1"><Label className="text-xs">Text when OFF</Label><Input className={cellInputCls} value={it.falseText || ''} onChange={(e) => updateItem(it.id, { falseText: e.target.value })} /></div>
                                    <label className="flex items-center gap-2 text-sm"><Switch checked={!!it.badWhenTrue} onCheckedChange={(v) => updateItem(it.id, { badWhenTrue: v })} /> ON is an alarm (red)</label>
                                  </>
                                )}
                                <label className="flex items-center gap-2 text-sm"><Switch checked={!!it.hidden} onCheckedChange={(v) => updateItem(it.id, { hidden: v })} /> Hide on client screen</label>
                                <div className="space-y-1 md:col-span-4"><Label className="text-xs">Note</Label><Input className={cellInputCls} value={it.note || ''} onChange={(e) => updateItem(it.id, { note: e.target.value })} /></div>
                              </div>
                            </td>
                          </tr>
                        ),
                      ];
                    })}
                  </tbody>
                </table>
              </div>
              {errors.length > 0 && (
                <ul className="space-y-0.5 text-sm text-red-600 dark:text-red-400">{errors.slice(0, 5).map((e) => <li key={e}>• {e}</li>)}</ul>
              )}
              <AnimatePresence>{testResult && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>{TestPanel}</motion.div>}</AnimatePresence>
            </TabsContent>
          </Tabs>

          <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-between">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving || testing}>Cancel</Button>
            <div className="flex gap-2">
              {step !== 'registers' && <Button variant="outline" onClick={() => setStep(step === 'controller' ? 'connection' : 'registers')}>Next</Button>}
              <Button onClick={save} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editing ? 'Save changes' : 'Add controller'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleting?.name}?</DialogTitle>
            <DialogDescription>The bridge stops polling this controller and it disappears from the client's Controllers screen.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}><Trash2 className="mr-2 h-4 w-4" /> Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
